// Primary navigation - always visible in header
type PrimaryNavItem = {
  name: string;
  href: string;
};

export const primaryNavItems: PrimaryNavItem[] = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Projects', href: '/projects' },
  { name: 'Blog', href: '/blog' },
];

// More dropdown - featured cards + list items
type FeaturedMoreItem = {
  type: 'featured';
  name: string;
  href: string;
  description: string;
  image: string;
  color: string;
};

type ListMoreItem = {
  type: 'list';
  name: string;
  href: string;
  description?: string;
};

export type MoreNavItem = FeaturedMoreItem | ListMoreItem;

export const moreNavItems: MoreNavItem[] = [
  // {
  //   type: 'featured',
  //   name: 'Books',
  //   href: '/#books',
  //   description: 'Reading list & recommendations',
  //   image: '/images/books/an-elegant-puzzle.jpg',
  //   color: 'bg-amber-50',
  // },
  {
    type: 'featured',
    name: 'Personal Stuff',
    href: '/personal-stuff',
    description: "Alfa's personal stuff & reco",
    image: '/images/films/letterboxd_tenet.jpg',
    color: 'bg-slate-900',
  },
  {
    type: 'featured',
    name: 'Services',
    href: '/services',
    description: 'What I can help you build',
    image: '/images/services_bg.webp',
    color: 'bg-blue-50',
  },
  {
    type: 'list',
    name: 'Attribution',
    href: '/attribution',
    description: 'Credits & licenses',
  },
];

export const experiencesData = [
  {
    id: 5,
    title: 'Fullstack AI Engineer',
    company: 'Arya Noble (ERHA & DERMIES) · Full time · Jakarta',
    highlights: [
      "Drove end to end architecture for ERHA & DERMIES' clinic POS, spanning front office, pharmacy, inventory, promo/loyalty, accounting, and payments, which processes ~8,000 to 10,000 transactions daily across 100+ branches.",
      'Owned end to end delivery of the Cashier Receipt Form system (shift/payment reporting, approvals, invoice correction, refunds) across 109 branches and ~400+ cashier accounts.',
      'Refactored a critically broken promo/voucher engine and overhauled a lossy payment logging pipeline, eliminating 100% of corrupted production data and cutting incident debug time by ~80%.',
      'Delivered BRI QR/EDC payment integration and owned Self & Advance Payment via Xendit, enabling ~200,000+ monthly transactions across 100+ clinics with full callback handling.',
      'Built Incident Brain, an AI system that analyzes production errors via hybrid RAG and automatically escalates a synthesized fix to Slack, cutting root cause diagnosis from ~120 minutes to under 10 (12× faster).',
    ],
    period: '2025 to Present',
    technologies: [
      'Kotlin Spring Boot',
      'Microservices',
      'RAG',
      'Xendit',
      'RabbitMQ',
      'PostgreSQL',
      'Redis',
      'AWS',
    ],
  },
  {
    id: 3,
    title: 'System Analyst',
    company: 'Astragraphia IT (Astra Group) · Full time · Hybrid',
    highlights: [
      "Led a squad of 5 engineers from across functions delivering PGN's enterprise CRM & Billing System, working directly with the Tech Lead on architecture, sprints, and delivery.",
      'Architected integration across 5 core modules and owned end to end delivery of Account Management, underpinning Rp 40+ trillion in annual revenue and 500,000+ subscribers across 19 provinces.',
      'Owned Functional Specification Design across 15+ stakeholder sessions (data models, DDL, UML, Figma mockups), reaching 100% sign off, zero rework cycles, and ~40% less development ambiguity.',
      'Delivered PoC presentations to senior PGN stakeholders (accelerating architecture sign off by ~3 weeks) and ensured production readiness with 200+ system integration test scenarios.',
    ],
    period: '2023 to 2025',
    technologies: [
      'System Analysis',
      'UML',
      'Java Spring Boot',
      'Oracle',
      'Figma',
    ],
  },
  {
    id: 2,
    title: 'Software Engineer',
    company: 'Astragraphia IT (Astra Group) · Full time · Hybrid',
    highlights: [
      'Built a secure user management module with JWT authentication, defined roles, and detailed privilege controls on Spring Boot & Oracle.',
      'Established a scalable boilerplate architecture adopted by ~10 developers, reducing onboarding ramp time by ~50% and standardizing patterns across the billing platform.',
    ],
    period: '2022 to 2023',
    technologies: ['Java Spring Boot', 'Oracle', 'SQL', 'Docker'],
  },
  {
    id: 1,
    title: 'Software Engineer Intern',
    company: 'Astra Life · Internship · Hybrid',
    highlights: [
      'Built parameterized monitoring agents (Spring Boot & Angular) for automated HTTP health checks, DB replication sync, and hardware/log analysis via cron jobs, cutting data load time by 77.5% (~80s → ~18s, validated with JMeter).',
      'Standardized production deployment via a Deployment Request Form consolidating Linux/SQL scripts into a single artifact, reducing deployment error risk.',
    ],
    period: '2022',
    technologies: ['Java Spring Boot', 'AngularJS', 'Oracle', 'SQL'],
  },
] as const;

export const filmsData = [
  {
    rank: 1,
    title: 'Tenet',
    director: 'Christopher Nolan',
    year: 2020,
    poster: '/images/films/letterboxd_tenet.jpg',
    note: 'The one that made me think about architecture, of both systems and stories.',
    link: 'https://letterboxd.com/film/tenet/',
  },
  {
    rank: 2,
    title: 'The Imitation Game',
    director: 'Morten Tyldum',
    year: 2014,
    poster: '/images/films/letterboxd_imitationgame.jpg',
    note: 'The obsession in every frame reminds me why craft matters, and what it costs.',
    link: 'https://letterboxd.com/film/the-imitation-game/',
  },
  {
    rank: 3,
    title: 'Eternal Sunshine of the Spotless Mind',
    director: 'Michel Gondry',
    year: 2004,
    poster: '/images/films/letterboxd_eternalsunshine.jpg',
    note: 'Memory as a system you can rewrite, and the damage you do when you try.',
    link: 'https://letterboxd.com/film/eternal-sunshine-of-the-spotless-mind/',
  },
  {
    rank: 4,
    title: 'Project Almanac',
    director: 'Dean Israelite',
    year: 2015,
    poster: '/images/films/letterboxd_projectalmanac.jpg',
    note: 'Found footage chaos that somehow still feels like a product demo gone wrong. I love the energy.',
    link: 'https://letterboxd.com/film/project-almanac/',
  },
  {
    rank: 5,
    title: 'The Social Network',
    director: 'David Fincher',
    year: 2010,
    poster: '/images/films/letterboxd_socialnetwork.jpg',
    note: 'Ambition, betrayal, and shipping under pressure. Still the sharpest film about building tech.',
    link: 'https://letterboxd.com/film/the-social-network/',
  },
] as const;

export const servicesData = [
  {
    id: 1,
    title: 'AI Engineering & LLM Integration',
    rate: 'Remote · open to discuss',
    description:
      'Agentic LLM workflows, search with retrieval augmentation, and systems that call tools, with orchestration across multiple models and full observability tracing.',
    deliverables: [
      'Agent loops with tool use and human escalation paths',
      'Hybrid RAG / search over your knowledge base',
      'Observability traces (LangSmith / Langfuse) so failures are debuggable',
      'Guardrails, eval hooks, and operator alerts where needed',
    ],
    fitFor: [
      'You need AI that ships in production, not a demo notebook',
      'Support, ops, or internal tools where latency and trust matter',
      'You want routing across multiple models with clear cost/latency tradeoffs',
    ],
    stack: ['Go', 'Kotlin', 'Python', 'PostgreSQL', 'pgvector', 'LangSmith'],
    outcome:
      'Example: Incident Brain cut root cause diagnosis from ~120 minutes to under 10 via hybrid RAG + Slack escalation.',
    isContact: false,
  },
  {
    id: 2,
    title: 'Backend & API Engineering',
    rate: 'Remote · open to discuss',
    description:
      'Scalable REST APIs, microservices, payment integrations, and reliable backend systems using Kotlin, Java Spring Boot, Go, or Node.js.',
    deliverables: [
      'Service design, APIs, and data models that match the domain',
      'Payment / webhook flows with idempotency and structured logging',
      'Messaging (RabbitMQ / queues) and cache layers where they earn their keep',
      'Hardening for production: retries, alerts, and deployable runbooks',
    ],
    fitFor: [
      'Clinic, fintech, or transactional systems with high throughput',
      'You are splitting a monolith or stabilizing a critical path',
      'Payments, inventory, or loyalty logic that cannot silently corrupt',
    ],
    stack: [
      'Kotlin',
      'Java Spring Boot',
      'Go',
      'Node.js',
      'PostgreSQL',
      'Redis',
      'RabbitMQ',
      'AWS',
    ],
    outcome:
      'Example: clinic POS + BRI/Xendit payments across 100+ branches, ~200k+ monthly transactions.',
    isContact: false,
  },
  {
    id: 3,
    title: 'Full-Stack Web Development',
    rate: 'Remote · open to discuss',
    description:
      'End to end web products built with Next.js, TypeScript, and Tailwind CSS, from design handoff to production deployment.',
    deliverables: [
      'Product UI with clear information architecture',
      'Auth, dashboards, and integrations wired to a real backend',
      'Payments, file storage, and analytics when the product needs them',
      'Deployed environments your team can operate',
    ],
    fitFor: [
      'SaaS MVPs and internal tools that need to look intentional',
      'You have Figma (or rough wires) and need a fast path to users',
      'Landing + product surfaces that should convert, not just exist',
    ],
    stack: [
      'Next.js',
      'TypeScript',
      'React',
      'Tailwind CSS',
      'PostgreSQL',
      'R2',
    ],
    outcome:
      'Example: Acaara, a digital invitation SaaS with templates, guest lists, and Midtrans payments in production.',
    isContact: false,
  },
  {
    id: 4,
    title: 'System Analysis & Architecture',
    rate: 'Remote · open to discuss',
    description:
      'Requirements gathering, UML diagrams, data modelling, and technical documentation that bridges business needs with engineering solutions.',
    deliverables: [
      'Stakeholder workshops turned into approved specs',
      'Physical data models, DDL guidance, and integration maps',
      'Figma / flow mockups that engineers can build from',
      'Test scenarios that prove the design before big build spend',
    ],
    fitFor: [
      'Enterprise programs that span many modules and stakeholders',
      'Ambiguity is burning sprint capacity',
      'You need a translator between business language and engineering',
    ],
    stack: ['UML', 'Figma', 'SQL', 'Java Spring Boot', 'Oracle'],
    outcome:
      'Example: PGN CRM & Billing: Account Management under Rp 40T+ annual revenue, 100% FSD sign off, zero rework cycles.',
    isContact: true,
  },
] as const;
